import { useState, useEffect } from 'react';
import { generateAPI, presetAPI } from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Sparkles, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

export const BatchGenerateModal = ({ open, onOpenChange, selectedVideoIds, onComplete }) => {
  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (open) {
      loadPresets();
    }
  }, [open]);

  const loadPresets = async () => {
    try {
      const res = await presetAPI.list();
      setPresets(res.data);
      if (res.data.length > 0) setSelectedPreset(res.data[0].id);
    } catch {
      toast.error('Failed to load presets');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedPreset) return;
    setGenerating(true);
    try {
      const res = await generateAPI.batchGenerate(selectedVideoIds, selectedPreset);
      toast.success(`Batch generation started for ${res.data.total} videos`);
      onOpenChange(false);
      onComplete?.(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Batch generation failed');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-border/50 max-w-md" data-testid="batch-generate-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Generate for Selected Videos</DialogTitle>
          <DialogDescription>
            Choose a preset to generate social post content for {selectedVideoIds.length} selected {selectedVideoIds.length === 1 ? 'video' : 'videos'}.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-3 py-2" data-testid="preset-select-list">
            {presets.map((preset) => (
              <div
                key={preset.id}
                className={`p-4 rounded-xl border cursor-pointer transition-all duration-200 ${
                  selectedPreset === preset.id
                    ? 'border-primary/40 bg-primary/5'
                    : 'border-white/5 bg-secondary/20 hover:border-white/10'
                }`}
                onClick={() => setSelectedPreset(preset.id)}
                data-testid={`select-preset-${preset.id}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <h4 className="text-sm font-semibold">{preset.name}</h4>
                  {selectedPreset === preset.id && (
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                  )}
                </div>
                <p className="text-xs text-muted-foreground line-clamp-1">{preset.description}</p>
                <div className="flex gap-1 mt-2">
                  {preset.targetPlatforms?.map((p, i) => (
                    <Badge key={i} variant="secondary" className="text-[9px] bg-indigo-500/10 text-indigo-400 border-0">{p}</Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="cancel-batch-generate">
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={generating || !selectedPreset}
            className="rounded-full bg-primary hover:bg-primary/90 shadow-lg shadow-indigo-500/25"
            data-testid="confirm-batch-generate"
          >
            {generating ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Starting...</>
            ) : (
              <><Sparkles className="w-4 h-4 mr-2" /> Generate for {selectedVideoIds.length} Videos</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
